/**
 * The guest keyring (decision 49's wrap records; principle 10; part 38a table KW; part 38c log, principles GP2 and GP3):
 * a random keyring key wrapped under the password's wrapping key (`deriveLoginKeys`), its associated data binding the
 * account, the keyring's ID and the KDF record it was derived under, so the relay can't hand one account's wrap to
 * another, swap keyrings or serve the wrap under another record (KW7); and each entry a person key sealed under the
 * keyring key, bound to the account, the keyring and the invite, so the relay can't move an entry between invites,
 * keyrings or accounts. The caller test-unwraps a wrap before sending it (principle 7). WebCrypto is injected.
 */
import { aesKey, field, INVITE_ID_BYTES, INVITE_KEY_BYTES, IV_BYTES, joined, lengthOf, TAG_BYTES } from './invite.ts';
import type { KdfRecord } from './kdf.ts';
import { PASSWORD_WRAPPING_BYTES } from './login.ts';

/** The keyring key: AES-256 (GP2). */
export const KEYRING_KEY_BYTES = 32;

/** A keyring's ID: 16 bytes (GP2). */
export const KEYRING_ID_BYTES = 16;

/** What the keyring key's wrap takes: its IV, the key and the tag (GP2). */
export const KEYRING_WRAP_BYTES = IV_BYTES + KEYRING_KEY_BYTES + TAG_BYTES;

/** What a keyring entry takes: its IV, the person key and the tag (GP2). */
export const SEALED_PERSON_KEY_BYTES = IV_BYTES + INVITE_KEY_BYTES + TAG_BYTES;

const encoder = new TextEncoder();
const KEYRING_DOMAIN = encoder.encode('cockatrice/guest-keyring/v1');
const PERSON_KEY_DOMAIN = encoder.encode('cockatrice/guest-keyring/person-key/v1');

/** The largest value a 4-byte field holds. */
const MAX_U32 = 0xffff_ffff;

/** The largest length a 2-byte length prefix holds. */
const MAX_U16 = 0xffff;

/** Where a keyring's wrap belongs (GP2): the account (its ID as the relay names it), the keyring's ID and the KDF record it is wrapped under. */
export interface KeyringPlace {
  readonly account: string;
  readonly id: Uint8Array;
  readonly kdf: KdfRecord;
}

/** Where a person key's entry belongs (GP2): the account, the keyring's ID and the invite's ID. */
export interface PersonKeyPlace {
  readonly account: string;
  readonly keyring: Uint8Array;
  readonly invite: Uint8Array;
}

function isWhole(value: number, max: number): boolean {
  return Number.isSafeInteger(value) && value >= 0 && value <= max;
}

/** Whether `account` fits {@link field}'s two-byte length, so it can never run into what follows. */
function accountOk(account: string): boolean {
  return encoder.encode(account).length <= MAX_U16;
}

/** Whether every field of `record` has an unambiguous encoding in {@link kdfBytes}. */
function kdfOk(record: KdfRecord): boolean {
  return isWhole(record.version, 0xff) && record.salt.length <= MAX_U16 && isWhole(record.memoryKiB, MAX_U32) && isWhole(record.passes, MAX_U32);
}

/** `record` as bytes, every field fixed-width or length-led: version (1), salt length (2) and salt, memory (4), passes (4), big-endian. */
function kdfBytes(record: KdfRecord): Uint8Array<ArrayBuffer> {
  const head = new Uint8Array(3);
  const view = new DataView(head.buffer);
  view.setUint8(0, record.version);
  view.setUint16(1, record.salt.length);
  const cost = new Uint8Array(8);
  const costView = new DataView(cost.buffer);
  costView.setUint32(0, record.memoryKiB);
  costView.setUint32(4, record.passes);
  return joined(head, record.salt, cost);
}

function keyringPlaceOk(place: KeyringPlace): boolean {
  return accountOk(place.account) && place.id.length === KEYRING_ID_BYTES && kdfOk(place.kdf);
}

function personPlaceOk(place: PersonKeyPlace): boolean {
  return accountOk(place.account) && place.keyring.length === KEYRING_ID_BYTES && place.invite.length === INVITE_ID_BYTES;
}

/** GP2: what a keyring's wrap is authenticated with: its domain, the account, the keyring's ID and the KDF record. */
function keyringData(place: KeyringPlace): Uint8Array<ArrayBuffer> {
  return joined(KEYRING_DOMAIN, field(place.account), place.id, kdfBytes(place.kdf));
}

/** GP2: what a keyring entry is authenticated with: its domain, the account, the keyring's ID and the invite's ID. */
function personData(place: PersonKeyPlace): Uint8Array<ArrayBuffer> {
  return joined(PERSON_KEY_DOMAIN, field(place.account), place.keyring, place.invite);
}

/** GP2: keyring key `key` wrapped at `place` under the password's wrapping key `wrapping`, with the fresh IV `iv`: the IV, then the ciphertext. */
export async function wrapKeyring(subtle: SubtleCrypto, wrapping: Uint8Array<ArrayBuffer>, place: KeyringPlace, key: Uint8Array<ArrayBuffer>, iv: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(wrapping, PASSWORD_WRAPPING_BYTES, 'a wrapping key');
  lengthOf(key, KEYRING_KEY_BYTES, 'a keyring key');
  lengthOf(iv, IV_BYTES, 'an IV');
  lengthOf(place.id, KEYRING_ID_BYTES, 'a keyring ID');
  if (!accountOk(place.account)) throw new RangeError('an account must be at most 65535 bytes');
  if (!kdfOk(place.kdf)) throw new RangeError('a KDF record must hold a 1-byte version, a salt of at most 65535 bytes and 4-byte costs');
  return joined(iv, new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv, additionalData: keyringData(place) }, await aesKey(subtle, wrapping), key)));
}

/** GP2, KW7: the keyring key a wrap at `place` holds, opened with wrapping key `wrapping`; null when it doesn't open (another place's or record's, altered, of the wrong length). */
export async function unwrapKeyring(subtle: SubtleCrypto, wrapping: Uint8Array<ArrayBuffer>, place: KeyringPlace, wrap: Uint8Array): Promise<Uint8Array<ArrayBuffer> | null> {
  if (wrapping.length !== PASSWORD_WRAPPING_BYTES || !keyringPlaceOk(place) || wrap.length !== KEYRING_WRAP_BYTES) return null;
  try {
    // A wrap of exactly KEYRING_WRAP_BYTES opens to exactly a key's bytes.
    return new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv: wrap.slice(0, IV_BYTES), additionalData: keyringData(place) }, await aesKey(subtle, wrapping), wrap.slice(IV_BYTES)));
  } catch {
    return null;
  }
}

/** GP2: person key `person` sealed as the entry at `place` under keyring key `keyringKey`, with the fresh IV `iv`. */
export async function sealPersonKey(subtle: SubtleCrypto, keyringKey: Uint8Array<ArrayBuffer>, place: PersonKeyPlace, person: Uint8Array<ArrayBuffer>, iv: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(keyringKey, KEYRING_KEY_BYTES, 'a keyring key');
  lengthOf(person, INVITE_KEY_BYTES, 'a person key');
  lengthOf(iv, IV_BYTES, 'an IV');
  lengthOf(place.keyring, KEYRING_ID_BYTES, 'a keyring ID');
  lengthOf(place.invite, INVITE_ID_BYTES, 'an invite ID');
  if (!accountOk(place.account)) throw new RangeError('an account must be at most 65535 bytes');
  return joined(iv, new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv, additionalData: personData(place) }, await aesKey(subtle, keyringKey), person)));
}

/** GP2: the person key the entry at `place` holds, opened with keyring key `keyringKey`; null when it doesn't open. */
export async function openPersonKey(subtle: SubtleCrypto, keyringKey: Uint8Array<ArrayBuffer>, place: PersonKeyPlace, sealed: Uint8Array): Promise<Uint8Array<ArrayBuffer> | null> {
  if (keyringKey.length !== KEYRING_KEY_BYTES || !personPlaceOk(place) || sealed.length !== SEALED_PERSON_KEY_BYTES) return null;
  try {
    return new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv: sealed.slice(0, IV_BYTES), additionalData: personData(place) }, await aesKey(subtle, keyringKey), sealed.slice(IV_BYTES)));
  } catch {
    return null;
  }
}
